import _ from "lodash";
/**-- external -- */
import React, { useState, useMemo } from "react";
import classNames from "classnames";
import {
  SearchInput,
  ChecklistDropdown,
  FilterLabelButton,
} from "@toddle-edu/ds-web";

/**-- internal -- */
import { useI18n } from "Hooks";
import { getLabelValueForMultiFilterLabelButton } from "Utils";

/**-- relative -- */
import classes from "./FilterHeader.scss";
import { columnIds } from "../GenericTableEditorUtils";

const DEBOUNCE_DURATION_FOR_SEARCH_TEXT_UPDATE = 200;

const SearchFilter = props => {
  const { value, onChange } = props;

  const { t } = useI18n(["common"]);

  const [searchText, setSearchText] = useState(value);

  const onChangeWithDebounced = useMemo(
    () => _.debounce(onChange, DEBOUNCE_DURATION_FOR_SEARCH_TEXT_UPDATE),
    [onChange]
  );

  const onUpdateSearchText = updatedSearchText => {
    setSearchText(updatedSearchText);
    onChangeWithDebounced(updatedSearchText);
  };

  return (
    <SearchInput
      value={searchText}
      onChange={onUpdateSearchText}
      size="medium"
      placeholder={t("common:search")}
    />
  );
};

const FilterHeader = props => {
  const {
    allowedFilters,
    filters = {},
    gradeOptions,
    tagOptions,
    areFiltersDisabled,
  } = props;

  const { t } = useI18n(["common"]);

  const [searchFilters, otherFilters] = _.partition(
    allowedFilters,
    filter => filter === "SEARCH_TEXT"
  );

  const getSelectedOptionsLabels = ({ options, value }) => {
    const selectedOptionsLabels = _.map(
      _.filter(options, item => _.includes(value, item.value)),
      option => option.label
    );

    return selectedOptionsLabels;
  };

  const renderFilters = ({ filter }) => {
    const onChangeFilter = value => {
      props.onChangeFilter({ filter, value });
    };

    switch (filter) {
      case "SEARCH_TEXT": {
        return (
          <div className={classes.searchContainer}>
            <SearchFilter value={filters[filter]} onChange={onChangeFilter} />
          </div>
        );
      }

      case columnIds.grades: {
        const formattedGradeOptions = _.map(gradeOptions, grade => ({
          label: grade.name,
          value: grade.id,
        }));

        return (
          <ChecklistDropdown
            options={formattedGradeOptions}
            value={filters[filter]}
            onChange={onChangeFilter}
            showAllOption={true}
            noResultLabel={t("common:no_label_found", {
              label: t("common:lowercase", {
                text: t("common:grade_plural"),
              }),
            })}
            isSearchEnabled={true}
            disabled={areFiltersDisabled}
          >
            <FilterLabelButton
              size={"small"}
              variant={"plain"}
              label={t("common:grade_plural")}
              labelValue={getLabelValueForMultiFilterLabelButton({
                options: formattedGradeOptions,
                selectedOptions: getSelectedOptionsLabels({
                  options: formattedGradeOptions,
                  value: filters[filter],
                }),
                t,
                allText: t("common:all_label", {
                  label: t("common:grade_plural"),
                }),
                noneText: t("common:none"),
              })}
            />
          </ChecklistDropdown>
        );
      }

      case columnIds.tags: {
        const formattedTagOptions = _.map(tagOptions, tag => ({
          label: tag.label,
          value: tag.id,
        }));

        return (
          <ChecklistDropdown
            options={formattedTagOptions}
            value={filters[filter]}
            onChange={onChangeFilter}
            showAllOption={true}
            isSearchEnabled={true}
            noResultLabel={t("common:no_label_found", {
              label: t("common:lowercase", {
                text: t("common:tags"),
              }),
            })}
            disabled={areFiltersDisabled}
          >
            <FilterLabelButton
              size="small"
              variant="plain"
              label={t("common:tags")}
              labelValue={getLabelValueForMultiFilterLabelButton({
                options: formattedTagOptions,
                selectedOptions: getSelectedOptionsLabels({
                  options: formattedTagOptions,
                  value: filters[filter],
                }),
                t,
                allText: t("common:all_label", {
                  label: t("common:tags"),
                }),
                noneText: t("common:none"),
              })}
            />
          </ChecklistDropdown>
        );
      }
    }
  };

  const containerClasses = classNames(classes.container, {
    [classes.containerWithSeachFilter]: _.size(searchFilters) > 0,
  });

  return (
    <div className={containerClasses}>
      {_.size(searchFilters) > 0
        ? renderFilters({ filter: _.first(searchFilters) })
        : null}
      <div className={classes.otherFilters}>
        {_.map(otherFilters, filter => (
          <React.Fragment key={filter}>
            {renderFilters({ filter })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default FilterHeader;
